"use client";

import type React from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";

interface ImageComparisonProps {
  beforeImage: string;
  afterImage: string;
  beforeAlt?: string;
  afterAlt?: string;
  beforeLabel?: string;
  afterLabel?: string;
  aspectRatio?: string;
  mobileAspectRatio?: string;
  className?: string;
  handleColor?: string;
  handleSize?: number;
  mobileHandleSize?: number;
  showLabels?: boolean;
  animateOnLoad?: boolean;
  animationDuration?: number;
  initialPosition?: number;
}

export function ImageComparison({
  beforeImage,
  afterImage,
  beforeAlt = "Before image",
  afterAlt = "After image",
  beforeLabel = "Original",
  afterLabel = "Reimagined",
  aspectRatio = "16/9",
  mobileAspectRatio,
  className,
  handleColor = "#f59e0b",
  handleSize = 48,
  mobileHandleSize = 36,
  showLabels = true,
  animateOnLoad = false,
  animationDuration = 1500,
  initialPosition = 50,
}: ImageComparisonProps) {
  const [sliderPosition, setSliderPosition] = useState(
    animateOnLoad ? 0 : initialPosition,
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(animateOnLoad);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Determine current aspect ratio and handle size based on device
  const currentAspectRatio =
    isMobile && mobileAspectRatio ? mobileAspectRatio : aspectRatio;
  const currentHandleSize = isMobile ? mobileHandleSize : handleSize;

  // Handle slider movement
  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (isAnimating) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const updateSliderPosition = (clientX: number) => {
    if (!containerRef.current || isAnimating) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const newPosition = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(newPosition);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    updateSliderPosition(e.clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !e.touches[0]) return;
    updateSliderPosition(e.touches[0].clientX);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isAnimating) return;
    const step = 5;

    if (e.key === "ArrowLeft") {
      setSliderPosition((prev) => Math.max(0, prev - step));
      e.preventDefault();
    } else if (e.key === "ArrowRight") {
      setSliderPosition((prev) => Math.min(100, prev + step));
      e.preventDefault();
    }
  };

  // Handle initial animation
  useEffect(() => {
    if (!animateOnLoad) return;

    const animationSteps = [
      { position: 0, delay: 0 },
      { position: 100, delay: animationDuration * 0.3 },
      { position: 0, delay: animationDuration * 0.6 },
      { position: initialPosition, delay: animationDuration * 0.9 },
    ];

    const cleanup: NodeJS.Timeout[] = [];

    animationSteps.forEach((step) => {
      const timeout = setTimeout(() => {
        setSliderPosition(step.position);
      }, step.delay);
      cleanup.push(timeout);
    });

    // End animation
    const finalTimeout = setTimeout(() => {
      setIsAnimating(false);
    }, animationDuration);
    cleanup.push(finalTimeout);

    return () => {
      cleanup.forEach((timeout) => clearTimeout(timeout));
    };
  }, [animateOnLoad, animationDuration, initialPosition]);

  // Add global event listeners for mouse/touch events
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      updateSliderPosition(e.clientX);
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (!isDragging || !e.touches[0]) return;
      updateSliderPosition(e.touches[0].clientX);
      e.preventDefault(); // Prevent scrolling while dragging
    };

    window.addEventListener("mouseup", handleGlobalMouseUp);
    window.addEventListener("touchend", handleGlobalMouseUp);
    window.addEventListener("mousemove", handleGlobalMouseMove);
    window.addEventListener("touchmove", handleGlobalTouchMove, {
      passive: false,
    });

    return () => {
      window.removeEventListener("mouseup", handleGlobalMouseUp);
      window.removeEventListener("touchend", handleGlobalMouseUp);
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("touchmove", handleGlobalTouchMove);
    };
  }, [isDragging, isAnimating]);

  return (
    <div className={cn("relative w-full overflow-hidden", className)}>
      {/* Container with aspect ratio */}
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio: currentAspectRatio }}
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        tabIndex={0}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={sliderPosition}
        aria-label="Image comparison slider"
        aria-valuetext={`${sliderPosition}% showing after image`}
        onKeyDown={handleKeyDown}
      >
        {/* Both images are positioned absolutely and take up the full container */}
        {/* Before image */}
        <div className="absolute inset-0 w-full h-full">
          <Image
            src={afterImage}
            alt={afterAlt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
          />
        </div>

        {/* After image with clip-path */}
        <div
          className={cn(
            "absolute inset-0 w-full h-full",
            isAnimating && "transition-[clip-path]",
          )}
          style={{
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
            transitionDuration: isAnimating
              ? `${animationDuration * 0.3}ms`
              : undefined,
          }}
        >
          <Image
            src={beforeImage}
            alt={beforeAlt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 50vw"
            priority
          />
        </div>

        {/* Labels */}
        {showLabels && (
          <>
            <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm font-semibold z-10">
              {beforeLabel}
            </div>
            <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-md text-sm font-semibold z-10">
              {afterLabel}
            </div>
          </>
        )}

        {/* Vertical divider line */}
        <div
          className={cn(
            "absolute top-0 bottom-0 w-0.5 bg-white z-10",
            isAnimating && "transition-[left]",
          )}
          style={{
            left: `${sliderPosition}%`,
            transitionDuration: isAnimating
              ? `${animationDuration * 0.3}ms`
              : undefined,
          }}
          aria-hidden="true"
        />

        {/* Slider handle */}
        <div
          className={cn(
            "absolute top-1/2 -translate-y-1/2 z-10",
            isAnimating && "transition-[left]",
          )}
          style={{
            left: `${sliderPosition}%`,
            transitionDuration: isAnimating
              ? `${animationDuration * 0.3}ms`
              : undefined,
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
          aria-hidden="true"
        >
          <div
            className="flex items-center justify-center rounded-full -translate-x-1/2 cursor-ew-resize shadow-lg"
            style={{
              width: `${currentHandleSize}px`,
              height: `${currentHandleSize}px`,
              backgroundColor: handleColor,
            }}
          >
            <svg
              width={Math.floor(currentHandleSize * 0.5)}
              height={Math.floor(currentHandleSize * 0.5)}
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-white"
            >
              <path
                d="M10 8L6 12M6 12L10 16M6 12H18M14 8L18 12M18 12L14 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {/* Screen reader instructions */}
        <span className="sr-only">
          Use left and right arrow keys to adjust the comparison slider between
          the before and after images.
        </span>
      </div>
    </div>
  );
}
